/** The Steps tab: the step list (order is for reading — `next` decides the
    flow), the read-only flow graph, and the inspector of the selected step. */

import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Copy, GitBranch, List } from 'lucide-react'
import FlowGraph from './FlowGraph'
import StepInspector from './StepInspector'
import { useWizardDesign } from './context'
import type { WizardModel, WizardStep, WizardStepKind } from './types'
import { KIND_META, STEP_KINDS, blankStep, newStepId, renameStep } from './types'
import { Badge, RemoveBtn, SelectInput, cardStyle, emptyHintStyle, hintStyle, iconBtn, smallBtn } from './ui'

export default function StepsTab({ onChange, selected, onSelect }: {
  onChange: (m: WizardModel) => void
  selected: string | null
  onSelect: (id: string | null) => void
}) {
  const { model, readOnly } = useWizardDesign()
  const [view, setView] = useState<'list' | 'graph'>('list')
  const [addKind, setAddKind] = useState<WizardStepKind>('input')
  const step = useMemo(() => model.steps.find((s) => s.id === selected) || null, [model.steps, selected])

  const setSteps = (steps: WizardStep[]) => onChange({ ...model, steps })
  const patchStep = (id: string, p: Partial<WizardStep>) => {
    setSteps(model.steps.map((s) => {
      if (s.id !== id) return s
      const merged = { ...s, ...p } as Record<string, unknown>
      // A key set to undefined is removed, so the file does not collect nulls.
      for (const k of Object.keys(merged)) if (merged[k] === undefined) delete merged[k]
      return merged as unknown as WizardStep
    }))
  }
  const add = () => {
    const id = newStepId(model, addKind)
    const at = step ? model.steps.indexOf(step) + 1 : model.steps.length
    const steps = [...model.steps]
    steps.splice(at, 0, blankStep(addKind, id))
    onChange({ ...model, steps, start: model.start || id })
    onSelect(id)
  }
  const duplicate = (s: WizardStep) => {
    const id = newStepId(model, s.kind)
    const steps = [...model.steps]
    steps.splice(model.steps.indexOf(s) + 1, 0, { ...JSON.parse(JSON.stringify(s)), id })
    setSteps(steps)
    onSelect(id)
  }
  const remove = (s: WizardStep) => {
    const steps = model.steps.filter((x) => x.id !== s.id)
    onChange({ ...model, steps, start: model.start === s.id ? (steps[0]?.id || '') : model.start })
    if (selected === s.id) onSelect(null)
  }
  const move = (i: number, d: number) => {
    const j = i + d
    if (j < 0 || j >= model.steps.length) return
    const steps = [...model.steps]
    ;[steps[i], steps[j]] = [steps[j], steps[i]]
    setSteps(steps)
  }

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', minHeight: 0 }}>
      <div style={{ flex: '0 0 280px', minWidth: 0 }}>
        <div style={{ ...cardStyle, padding: 8 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            <button type="button" style={{ ...smallBtn, ...(view === 'list' ? { borderColor: 'var(--color-accent)' } : {}) }}
              onClick={() => setView('list')}><List size={12} /> Inspector</button>
            <button type="button" style={{ ...smallBtn, ...(view === 'graph' ? { borderColor: 'var(--color-accent)' } : {}) }}
              onClick={() => setView('graph')}><GitBranch size={12} /> Flow</button>
          </div>
          {!readOnly && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <SelectInput value={addKind} onChange={(k) => setAddKind(k as WizardStepKind)}
                  options={STEP_KINDS.map((k) => ({ value: k, label: KIND_META[k].label }))} />
              </div>
              <button type="button" style={smallBtn} onClick={add} title="Add after the selected step">Add</button>
            </div>
          )}
          {model.steps.length === 0 && <div style={emptyHintStyle}>No steps yet — add the first one.</div>}
          {model.steps.map((s, i) => {
            const sel = s.id === selected
            return (
              <div key={s.id} onClick={() => onSelect(s.id)} data-tw-wizard-design-step={s.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '5px 6px', marginBottom: 2,
                  borderRadius: 4, cursor: 'pointer',
                  background: sel ? 'var(--color-bg-secondary)' : 'transparent',
                  border: `1px solid ${sel ? 'var(--color-accent)' : 'transparent'}`,
                }}>
                <span className="material-icons" style={{ fontSize: 17, color: 'var(--color-text-secondary)' }}>
                  {KIND_META[s.kind]?.icon || 'circle'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.title || KIND_META[s.kind]?.label || s.kind}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>
                    {s.id} {model.start === s.id && <Badge tone="ok">start</Badge>}
                  </div>
                </div>
                {!readOnly && sel && (
                  <span style={{ display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
                    <button type="button" style={iconBtn} title="Move up" onClick={() => move(i, -1)}><ArrowUp size={12} /></button>
                    <button type="button" style={iconBtn} title="Move down" onClick={() => move(i, 1)}><ArrowDown size={12} /></button>
                    <button type="button" style={iconBtn} title="Duplicate" onClick={() => duplicate(s)}><Copy size={12} /></button>
                    <RemoveBtn title="Delete step" onClick={() => remove(s)} />
                  </span>
                )}
              </div>
            )
          })}
          {step && !readOnly && model.start !== step.id && (
            <button type="button" style={{ ...smallBtn, marginTop: 6 }} onClick={() => onChange({ ...model, start: step.id })}>
              Start here
            </button>
          )}
          <div style={hintStyle}>Order is only for reading — each step’s Next decides where the wizard goes.</div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {view === 'graph' ? (
          <FlowGraph selected={selected} onSelect={(id) => { onSelect(id); setView('list') }} />
        ) : step ? (
          // Keyed by position, not id: renaming a step must not remount the
          // inspector (and lose the cursor in the id field) on every keystroke.
          <StepInspector key={model.steps.indexOf(step)} step={step}
            onChange={(p) => patchStep(step.id, p)}
            onRename={(id) => {
              if (model.steps.some((s) => s.id === id && s !== step)) return
              onChange(renameStep(model, step.id, id))
              onSelect(id)
            }} />
        ) : (
          <div style={{ ...cardStyle, ...emptyHintStyle }}>Pick a step on the left, or open the flow.</div>
        )}
      </div>
    </div>
  )
}
